'use client';

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { preferencesService } from '@/lib/preferences-service';
import { 
  type ThemeMode, 
  type ThemeName, 
  DEFAULT_THEME, 
  DEFAULT_MODE, 
  getThemeColors,
  THEMES 
} from '@/lib/themes';

interface ThemeContextValue {
  theme: ThemeName;
  mode: ThemeMode;
  resolvedMode: 'dark' | 'light';
  setTheme: (theme: ThemeName) => void;
  setMode: (mode: ThemeMode) => void;
  themes: typeof THEMES;
  isSyncing: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Legacy storage keys - kept for backwards compatibility
const STORAGE_KEY_THEME = 'fieldwatch-theme';
const STORAGE_KEY_MODE = 'fieldwatch-mode';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(DEFAULT_THEME);
  const [mode, setModeState] = useState<ThemeMode>(DEFAULT_MODE);
  const [resolvedMode, setResolvedMode] = useState<'dark' | 'light'>('dark');
  const [mounted, setMounted] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Load saved preferences on mount.
  //
  // Always flips ``mounted`` to true within 250 ms even if the
  // preferences backend hangs — the whole app is hidden
  // (visibility:hidden) until mount, so a stalled ``getTheme()``
  // would present as "blank page / theme not loading". The
  // timeout guarantees defaults render, and any late-arriving
  // preferences silently override via setState.
  useEffect(() => {
    let alreadyMounted = false;
    const markMounted = () => {
      if (alreadyMounted) return;
      alreadyMounted = true;
      setMounted(true);
    };

    const safetyTimer = window.setTimeout(markMounted, 250);

    const loadPreferences = async () => {
      try {
        // Try preferences service first (supports both localStorage and API)
        const themePrefs = await preferencesService.getTheme();

        if (themePrefs.themeName && THEMES.some(t => t.name === themePrefs.themeName)) {
          setThemeState(themePrefs.themeName);
        }
        if (themePrefs.themeMode && ['dark', 'light', 'system'].includes(themePrefs.themeMode)) {
          setModeState(themePrefs.themeMode);
        }
      } catch {
        // Fallback to direct localStorage if service fails
        const savedTheme = localStorage.getItem(STORAGE_KEY_THEME) as ThemeName | null;
        const savedMode = localStorage.getItem(STORAGE_KEY_MODE) as ThemeMode | null;

        if (savedTheme && THEMES.some(t => t.name === savedTheme)) {
          setThemeState(savedTheme);
        }
        if (savedMode && ['dark', 'light', 'system'].includes(savedMode)) {
          setModeState(savedMode);
        }
      }

      markMounted();
    };

    loadPreferences();

    return () => window.clearTimeout(safetyTimer);
  }, []);

  // Resolve system preference
  useEffect(() => {
    if (!mounted) return undefined;

    const resolveMode = (): 'dark' | 'light' => {
      if (mode === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return mode;
    };

    setResolvedMode(resolveMode());

    // Listen for system theme changes
    if (mode !== 'system') return undefined;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setResolvedMode(resolveMode());
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode, mounted]);

  // Apply theme CSS variables
  useEffect(() => {
    if (!mounted) return;

    const colors = getThemeColors(theme, resolvedMode);
    const root = document.documentElement;

    // Set color scheme
    root.classList.remove('dark', 'light');
    root.classList.add(resolvedMode);

    // Apply theme colors as CSS custom properties
    root.style.setProperty('--primary', colors.primary);
    root.style.setProperty('--primary-foreground', colors.primaryForeground);
    root.style.setProperty('--ring', colors.ring);
    root.style.setProperty('--chart-1', colors.chart1);
    root.style.setProperty('--chart-2', colors.chart2);
    root.style.setProperty('--chart-3', colors.chart3);
    root.style.setProperty('--chart-4', colors.chart4);
    root.style.setProperty('--chart-5', colors.chart5);
    root.style.setProperty('--success', colors.success);
    root.style.setProperty('--warning', colors.warning);
    root.style.setProperty('--info', colors.info);
    root.style.setProperty('--glow', colors.glow);

    // Apply mode-specific base colors
    if (resolvedMode === 'dark') {
      root.style.setProperty('--background', '#09090B');
      root.style.setProperty('--foreground', '#FAFAFA');
      root.style.setProperty('--card', 'rgba(17, 17, 21, 0.8)');
      root.style.setProperty('--card-foreground', '#FAFAFA');
      root.style.setProperty('--popover', '#111115');
      root.style.setProperty('--popover-foreground', '#FAFAFA');
      root.style.setProperty('--secondary', '#18181B');
      root.style.setProperty('--secondary-foreground', '#A1A1AA');
      root.style.setProperty('--muted', '#18181B');
      root.style.setProperty('--muted-foreground', '#71717A');
      root.style.setProperty('--accent', '#1C1C21');
      root.style.setProperty('--accent-foreground', '#FAFAFA');
      root.style.setProperty('--border', 'rgba(255, 255, 255, 0.06)');
      root.style.setProperty('--input', '#1C1C21');
      root.style.setProperty('--sidebar', '#0C0C0E');
      root.style.setProperty('--sidebar-foreground', '#FAFAFA');
      root.style.setProperty('--sidebar-accent', '#1C1C21');
      root.style.setProperty('--sidebar-accent-foreground', '#FAFAFA');
      root.style.setProperty('--sidebar-border', 'rgba(255, 255, 255, 0.04)');
    } else {
      root.style.setProperty('--background', '#FFFFFF');
      root.style.setProperty('--foreground', '#09090B');
      root.style.setProperty('--card', '#FFFFFF');
      root.style.setProperty('--card-foreground', '#09090B');
      root.style.setProperty('--popover', '#FFFFFF');
      root.style.setProperty('--popover-foreground', '#09090B');
      root.style.setProperty('--secondary', '#F4F4F5');
      root.style.setProperty('--secondary-foreground', '#18181B');
      root.style.setProperty('--muted', '#F4F4F5');
      root.style.setProperty('--muted-foreground', '#71717A');
      root.style.setProperty('--accent', '#F4F4F5');
      root.style.setProperty('--accent-foreground', '#18181B');
      root.style.setProperty('--border', 'rgba(0, 0, 0, 0.08)');
      root.style.setProperty('--input', '#F4F4F5');
      root.style.setProperty('--sidebar', '#FAFAFA');
      root.style.setProperty('--sidebar-foreground', '#09090B');
      root.style.setProperty('--sidebar-accent', '#F4F4F5');
      root.style.setProperty('--sidebar-accent-foreground', '#18181B');
      root.style.setProperty('--sidebar-border', 'rgba(0, 0, 0, 0.06)');
    }

    // Set sidebar primary to match theme primary
    root.style.setProperty('--sidebar-primary', colors.primary);
    root.style.setProperty('--sidebar-primary-foreground', colors.primaryForeground);
    root.style.setProperty('--sidebar-ring', colors.ring);

  }, [theme, resolvedMode, mounted]);

  const setTheme = useCallback(async (newTheme: ThemeName) => {
    setThemeState(newTheme);
    setIsSyncing(true);
    
    try {
      // Save through preferences service (works with both localStorage and API)
      await preferencesService.setTheme({ themeName: newTheme });
    } catch {
      // Fallback to direct localStorage
      localStorage.setItem(STORAGE_KEY_THEME, newTheme);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  const setMode = useCallback(async (newMode: ThemeMode) => {
    setModeState(newMode);
    setIsSyncing(true);
    
    try {
      // Save through preferences service (works with both localStorage and API)
      await preferencesService.setTheme({ themeMode: newMode });
    } catch {
      // Fallback to direct localStorage
      localStorage.setItem(STORAGE_KEY_MODE, newMode);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    mode,
    resolvedMode,
    setTheme,
    setMode,
    themes: THEMES,
    isSyncing,
  }), [theme, mode, resolvedMode, setTheme, setMode, isSyncing]);

  // Always wrap children in context — even before mount, so useTheme() never throws.
  // visibility:hidden prevents flash of wrong theme until CSS vars are applied.
  return (
    <ThemeContext.Provider value={value}>
      {!mounted ? (
        <div style={{ visibility: 'hidden' }}>{children}</div>
      ) : (
        children
      )}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
